import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MdOutlineArrowDropDown, MdCheck } from 'react-icons/md';
import { RiDeleteBin6Line } from "react-icons/ri";
import { TiArrowUnsorted } from 'react-icons/ti';
import { FaPlus } from "react-icons/fa";
import { useCurrency } from '../../../../../context/currencyContext';
import Swal from 'sweetalert2'; // For beautiful alerts

const ShippingTypes = () => {
  const navigate = useNavigate();
  const { formatAmount } = useCurrency();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [userLabId, setUserLabId] = useState('');
  const itemsPerPage = 3;

  // Load userLabId from localStorage on mount
  useEffect(() => {
    const labId = localStorage.getItem('userLabId');
    if (labId) {
      setUserLabId(labId);
    }
  }, []);

  // Fetch shipping types (filtered by userLabId if available)
  useEffect(() => {
    const fetchData = async () => {
      try {
        let url = 'https://hms.backend.hiragroupcloud.com/api/shipping';
        if (userLabId) {
          url += `?addedBy=${userLabId}`;
        }
        const res = await fetch(url);
        const json = await res.json();
       
        if (json.success) {
          setData(json.data || []);
        } else {
          Swal.fire('Error', 'Failed to load shipping types', 'error');
        }
      } catch (err) {
        Swal.fire('Error', 'Network error. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };
   
    fetchData();
  }, [userLabId]);

  // Delete shipping type with SweetAlert confirmation and success message
  const handleDelete = async (id, service) => {
    // Permission check: only allow deleting own entries if userLabId exists
    if (userLabId && data.find(item => item._id === id)?.addedBy !== userLabId) {
      Swal.fire('Access Denied', 'You can only delete your own shipping types', 'warning');
      return;
    }

    // Confirmation dialog (like first image)
    const result = await Swal.fire({
      title: 'Confirm Deletion',
      text: `Are you sure you want to delete shipping type "${service}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'No',
      reverseButtons: true,
      customClass: {
        confirmButton: 'btn btn-danger mx-2',
        cancelButton: 'btn btn-secondary mx-2',
      },
      buttonsStyling: false
    });

    if (!result.isConfirmed) return;

    try {
      const res = await fetch(`https://hms.backend.hiragroupcloud.com/api/shipping/${id}`, {
        method: 'DELETE'
      });
      const json = await res.json();
     
      if (json.success) {
        setData(prev => prev.filter(item => item._id !== id));

        // Success dialog (like second image)
        Swal.fire({
          title: 'Success!',
          text: 'Shipping type deleted successfully!',
          icon: 'success',
          confirmButtonColor: '#28a745',
          customClass: {
            confirmButton: 'btn btn-success'
          },
          buttonsStyling: false
        });
      } else {
        Swal.fire('Error', 'Delete failed', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Delete failed due to network issue', 'error');
    }
  };

  // Navigate to assign accounts page
  const handleAssignToAccounts = (item) => {
    navigate(`/assign-accounts/${item._id}`, {
      state: {
        shippingType: item.service,
        shippingId: item._id
      }
    });
  };

  // Sort data based on column
  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;
    if (sortConfig.key === 'price') {
      const aValue = parseFloat(a[sortConfig.key]) || 0;
      const bValue = parseFloat(b[sortConfig.key]) || 0;
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    const aValue = a[sortConfig.key]?.toString().toLowerCase() || '';
    const bValue = b[sortConfig.key]?.toString().toLowerCase() || '';
    return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  });

  // Filter data based on search term
  const filteredData = sortedData.filter(item =>
    item.service?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentData = filteredData.slice(startIndex, endIndex);

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Navigate to specific page
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Navigate to add new shipping type
  const handleAddClick = () => {
    navigate('/AddShippingType');
  };

  // Toggle dropdown menu
  const toggleDropdown = (id) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  // Edit shipping type (with permission check)
  const handleEditClick = (item) => {
    if (userLabId && item.addedBy !== userLabId) {
      Swal.fire('Access Denied', 'You can only edit your own shipping types', 'warning');
      return;
    }
    navigate(`/shipping-edit/${item._id}`);
  };

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return <TiArrowUnsorted style={{ marginLeft: '5px', opacity: 0.3 }} />;
    }
    return (
      <TiArrowUnsorted
        style={{
          marginLeft: '5px',
          transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s'
        }}
      />
    );
  };

  // Format price for display
  const formatPrice = (price) => {
    if (price === null || price === undefined || price === '') return formatAmount(0);
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    return formatAmount(isNaN(numericPrice) ? 0 : numericPrice);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Button styles
  const btnStyle = (disabled) => ({
    padding: '8px 14px',
    border: '1px solid #dee2e6',
    backgroundColor: 'white',
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s',
  });

  const pageBtnStyle = {
    ...btnStyle(false),
    minWidth: '36px',
  };

  const activePageStyle = {
    ...pageBtnStyle,
    backgroundColor: '#004aad',
    color: 'white',
    borderColor: '#004aad',
    fontWeight: '700',
  };

  // Loading state
  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div>
      <h1>Shipping Types</h1>
     
      <div style={{ backgroundColor: '#f4f4f4', padding: '10px', height: '500px', borderRadius: '5px' ,width:'1560px'}}>
        <div style={{ backgroundColor: 'white', padding: '10px', height: '435px', borderRadius: '5px', overflow: 'auto' }}>
          {/* Search and Add Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <input
              type="text"
              placeholder="Type to search"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{
                width: '70%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                fontSize: '14px',
              }}
            />
            <button
              onClick={handleAddClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#004aad',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#004aad'}
            >
              <FaPlus /> Add
            </button>
          </div>
         
          {/* Shipping Types Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', backgroundColor: '#f4f4f4' }}>
            <thead>
              <tr style={{ backgroundColor: '#f4f4f4', textAlign: 'left' }}>
                {[
                  { key: 'service', label: 'Service' },
                  { key: 'company', label: 'Company' },
                  { key: 'inboundDays', label: 'Inbound Transit Days' },
                  { key: 'outboundDays', label: 'Outbound Transit Days' },
                  { key: 'price', label: 'Price' },
                  { key: null, label: '' }
                ].map((header, i) => (
                  <th
                    key={i}
                    style={{
                      padding: '8px',
                      borderBottom: '1px solid #ddd',
                      fontWeight: '700',
                      color: '#495057',
                      whiteSpace: 'nowrap',
                      cursor: header.key ? 'pointer' : 'default',
                      userSelect: 'none',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={header.key ? () => handleSort(header.key) : undefined}
                    onMouseEnter={header.key ? (e) => e.currentTarget.style.backgroundColor = '#f8f9fa' : undefined}
                    onMouseLeave={header.key ? (e) => e.currentTarget.style.backgroundColor = 'transparent' : undefined}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {header.label}
                      {header.key && getSortIndicator(header.key)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((item) => (
                  <tr key={item._id}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{item.service}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{item.company}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{item.inboundDays}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{item.outboundDays} Days</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{formatPrice(item.price)}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                      <div className="dropdown-container" style={{ position: 'relative', display: 'inline-flex', gap: '0' }}>
                        {/* Edit Button */}
                        <button
                          onClick={() => handleEditClick(item)}
                          style={{
                            backgroundColor: '#004aad',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px 0 0 6px',
                            padding: '5px 10px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#003a8c'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#004aad'}
                        >
                          Edit
                        </button>
                        {/* Dropdown Arrow */}
                        <button
                          onClick={() => toggleDropdown(item._id)}
                          style={{
                            backgroundColor: '#004aad',
                            color: 'white',
                            border: 'none',
                            borderLeft: '1px solid #004aad',
                            borderRadius: '0 6px 6px 0',
                            padding: '5px 8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#003a8c'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#004aad'}
                        >
                          <MdOutlineArrowDropDown style={{ fontSize: '18px' }} />
                        </button>
                        {/* Dropdown Menu */}
                        {openDropdown === item._id && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '5px',
                            backgroundColor: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            minWidth: '200px',
                            overflow: 'hidden'
                          }}>
                            <button
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                width: '100%',
                                padding: '10px 16px',
                                backgroundColor: 'white',
                                border: 'none',
                                textAlign: 'left',
                                fontSize: '14px',
                                color: '#212529',
                                cursor: 'pointer',
                                transition: 'background 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                              onClick={() => {
                                handleAssignToAccounts(item);
                                setOpenDropdown(null);
                              }}
                            >
                              <MdCheck style={{ color: '#333' }} />
                              Assign To Accounts
                            </button>
                            <button
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                width: '100%',
                                padding: '10px 16px',
                                backgroundColor: 'white',
                                border: 'none',
                                textAlign: 'left',
                                fontSize: '14px',
                                color: '#333',
                                cursor: 'pointer',
                                transition: 'background 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8d7da'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                              onClick={() => {
                                handleDelete(item._id, item.service);
                                setOpenDropdown(null);
                              }}
                            >
                              <RiDeleteBin6Line />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                    No shipping types found. Click "Add" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
         
          {/* Pagination */}
          {totalItems > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '25px',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <div style={{ color: '#333', fontSize: '16px', fontWeight: '500' }}>
                Showing {totalItems > 0 ? startIndex + 1 : 0} to {endIndex} of {totalItems} entries
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  style={btnStyle(currentPage === 1)}
                  onMouseEnter={(e) => !(currentPage === 1) && (e.target.style.backgroundColor = '#f8f9fa')}
                  onMouseLeave={(e) => !(currentPage === 1) && (e.target.style.backgroundColor = 'white')}
                >
                  First
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={btnStyle(currentPage === 1)}
                  onMouseEnter={(e) => !(currentPage === 1) && (e.target.style.backgroundColor = '#f8f9fa')}
                  onMouseLeave={(e) => !(currentPage === 1) && (e.target.style.backgroundColor = 'white')}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      style={currentPage === page ? activePageStyle : pageBtnStyle}
                      onMouseEnter={(e) => currentPage !== page && (e.target.style.backgroundColor = '#f8f9fa')}
                      onMouseLeave={(e) => currentPage !== page && (e.target.style.backgroundColor = 'white')}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={btnStyle(currentPage === totalPages)}
                  onMouseEnter={(e) => !(currentPage === totalPages) && (e.target.style.backgroundColor = '#f8f9fa')}
                  onMouseLeave={(e) => !(currentPage === totalPages) && (e.target.style.backgroundColor = 'white')}
                >
                  Next
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={btnStyle(currentPage === totalPages)}
                  onMouseEnter={(e) => !(currentPage === totalPages) && (e.target.style.backgroundColor = '#f8f9fa')}
                  onMouseLeave={(e) => !(currentPage === totalPages) && (e.target.style.backgroundColor = 'white')}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShippingTypes;